import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

const WorkflowContext = createContext();

const initialState = {
  workflows: {},
  currentWorkflowId: null,
  nodes: [],
  connections: [],
  providers: {
    openai_gpt4: {
      name: 'OpenAI GPT-4',
      type: 'text_generation',
      config: { model: 'gpt-4', max_tokens: 1000 }
    },
    anthropic_claude: {
      name: 'Anthropic Claude',
      type: 'text_generation',
      config: { model: 'claude-3-sonnet', max_tokens: 1000 }
    },
    elevenlabs_tts: {
      name: 'ElevenLabs TTS',
      type: 'text_to_speech',
      config: { voice: 'default', stability: 0.5 }
    },
    stable_video: {
      name: 'Stable Video',
      type: 'video_generation',
      config: { model: 'svd', fps: 24 }
    },
    runway_gen: {
      name: 'Runway Gen',
      type: 'video_generation',
      config: { model: 'gen-2', duration: 4 }
    },
    pika_labs: {
      name: 'Pika Labs',
      type: 'video_generation',
      config: { model: 'pika-1.0', duration: 3 }
    }
  }
};

const workflowReducer = (state, action) => {
  switch (action.type) {
    case 'SET_WORKFLOWS':
      return { ...state, workflows: action.payload };
    
    case 'SET_CURRENT_WORKFLOW':
      return { ...state, currentWorkflowId: action.payload };
    
    case 'ADD_WORKFLOW':
      return {
        ...state,
        workflows: {
          ...state.workflows,
          [action.payload.id]: action.payload
        }
      };
    
    case 'UPDATE_WORKFLOW':
      return {
        ...state,
        workflows: {
          ...state.workflows,
          [action.payload.id]: action.payload
        }
      };
    
    case 'DELETE_WORKFLOW':
      const { [action.payload]: deleted, ...remainingWorkflows } = state.workflows;
      return {
        ...state,
        workflows: remainingWorkflows,
        currentWorkflowId: state.currentWorkflowId === action.payload ? null : state.currentWorkflowId
      };
    
    case 'ADD_NODE':
      const newNode = {
        id: uuidv4(),
        type: action.payload.type,
        position: action.payload.position,
        settings: action.payload.settings || {},
        status: 'pending',
        createdAt: new Date().toISOString()
      };
      return {
        ...state,
        nodes: [...state.nodes, newNode]
      };
    
    case 'UPDATE_NODE':
      return {
        ...state,
        nodes: state.nodes.map(node =>
          node.id === action.payload.id
            ? { ...node, ...action.payload }
            : node
        )
      };
    
    case 'DELETE_NODE':
      return {
        ...state,
        nodes: state.nodes.filter(node => node.id !== action.payload),
        connections: state.connections.filter(
          conn => conn.fromNode !== action.payload && conn.toNode !== action.payload
        )
      };
    
    case 'ADD_CONNECTION':
      const newConnection = {
        id: uuidv4(),
        fromNode: action.payload.fromNode,
        fromOutput: action.payload.fromOutput,
        toNode: action.payload.toNode,
        toInput: action.payload.toInput,
        createdAt: new Date().toISOString()
      };
      return {
        ...state,
        connections: [...state.connections, newConnection]
      };
    
    case 'DELETE_CONNECTION':
      return {
        ...state,
        connections: state.connections.filter(conn => conn.id !== action.payload)
      };
    
    case 'CLEAR_CANVAS':
      return {
        ...state,
        nodes: [],
        connections: []
      };
    
    case 'SET_NODES':
      return { ...state, nodes: action.payload };
    
    case 'SET_CONNECTIONS':
      return { ...state, connections: action.payload };
    
    default:
      return state;
  }
};

export const WorkflowProvider = ({ children }) => {
  const [state, dispatch] = useReducer(workflowReducer, initialState);

  // Load workflows from localStorage on component mount
  useEffect(() => {
    const loadWorkflows = () => {
      try {
        const savedWorkflows = localStorage.getItem('cartoon_movie_studio_workflows');
        if (savedWorkflows) {
          const workflows = JSON.parse(savedWorkflows);
          dispatch({ type: 'SET_WORKFLOWS', payload: workflows });
          
          // Set current workflow if exists
          const currentWorkflowId = localStorage.getItem('cartoon_movie_studio_current_workflow');
          if (currentWorkflowId && workflows[currentWorkflowId]) {
            dispatch({ type: 'SET_CURRENT_WORKFLOW', payload: currentWorkflowId });
          }
        } else {
          // Create a default workflow
          const defaultWorkflow = {
            id: uuidv4(),
            name: 'My First Workflow',
            description: 'A sample workflow to get you started',
            status: 'draft',
            createdAt: new Date().toISOString()
          };
          dispatch({ type: 'ADD_WORKFLOW', payload: defaultWorkflow });
          dispatch({ type: 'SET_CURRENT_WORKFLOW', payload: defaultWorkflow.id });
          saveWorkflowsToStorage({ [defaultWorkflow.id]: defaultWorkflow });
        }
      } catch (error) {
        console.error('Error loading workflows:', error);
      }
    };
    
    loadWorkflows();
  }, []);

  // Save workflows to localStorage
  const saveWorkflowsToStorage = (workflows) => {
    try {
      localStorage.setItem('cartoon_movie_studio_workflows', JSON.stringify(workflows));
    } catch (error) {
      console.error('Error saving workflows:', error);
    }
  };

  // Save current workflow to localStorage
  const saveCurrentWorkflowToStorage = (workflowId) => {
    try {
      localStorage.setItem('cartoon_movie_studio_current_workflow', workflowId);
    } catch (error) {
      console.error('Error saving current workflow:', error);
    }
  };

  const createWorkflow = async (name, description = '') => {
    const workflow = {
      id: uuidv4(),
      name,
      description,
      status: 'draft',
      createdAt: new Date().toISOString()
    };

    dispatch({ type: 'ADD_WORKFLOW', payload: workflow });
    dispatch({ type: 'SET_CURRENT_WORKFLOW', payload: workflow.id });
    
    // Save to localStorage
    const updatedWorkflows = { ...state.workflows, [workflow.id]: workflow };
    saveWorkflowsToStorage(updatedWorkflows);
    saveCurrentWorkflowToStorage(workflow.id);
    
    return workflow;
  };

  const addNode = async (type, position, settings = {}) => {
    const node = {
      type,
      position,
      settings
    };

    dispatch({ type: 'ADD_NODE', payload: node });
    return node;
  };

  const updateNode = async (nodeId, updates) => {
    const updateData = { id: nodeId, ...updates };
    dispatch({ type: 'UPDATE_NODE', payload: updateData });
  };

  const deleteNode = async (nodeId) => {
    dispatch({ type: 'DELETE_NODE', payload: nodeId });
  };

  const addConnection = async (fromNode, fromOutput, toNode, toInput) => {
    const connection = {
      fromNode,
      fromOutput,
      toNode,
      toInput
    };

    dispatch({ type: 'ADD_CONNECTION', payload: connection });
  };

  const deleteConnection = async (connectionId) => {
    dispatch({ type: 'DELETE_CONNECTION', payload: connectionId });
  };

  const executeWorkflow = async (workflowId) => {
    // Simulate workflow execution
    const workflow = state.workflows[workflowId];
    if (!workflow) {
      throw new Error('Workflow not found');
    }

    // Update workflow status to running
    dispatch({ type: 'UPDATE_WORKFLOW', payload: { ...workflow, status: 'running' } });

    // Simulate processing time
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Update workflow status to completed
    dispatch({ type: 'UPDATE_WORKFLOW', payload: { ...workflow, status: 'completed' } });

    // Save to localStorage
    const updatedWorkflows = { ...state.workflows, [workflowId]: { ...workflow, status: 'completed' } };
    saveWorkflowsToStorage(updatedWorkflows);

    return { status: 'completed', message: 'Workflow executed successfully!' };
  };

  const setCurrentWorkflow = (workflowId) => {
    dispatch({ type: 'SET_CURRENT_WORKFLOW', payload: workflowId });
    saveCurrentWorkflowToStorage(workflowId);
  };

  const clearCanvas = () => {
    dispatch({ type: 'CLEAR_CANVAS' });
  };

  const value = {
    ...state,
    createWorkflow,
    addNode,
    updateNode,
    deleteNode,
    addConnection,
    deleteConnection,
    executeWorkflow,
    setCurrentWorkflow,
    clearCanvas
  };

  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  );
};

export const useWorkflow = () => {
  const context = useContext(WorkflowContext);
  if (!context) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};
